import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { MessageSquarePlus, Plus, Send, Sparkles, Truck } from 'lucide-react'
import { api, errorMessage } from '@/lib/api'
import { cn } from '@/lib/cn'
import { formatDate, formatDateTime, formatDateTimeShort } from '@/lib/date'
import { money } from '@/lib/money'
import { useAuth } from '@/context/AuthContext'
import type { Order, Paginated } from '@/types'
import PageHeader from '@/components/ui/PageHeader'
import Button from '@/components/ui/Button'
import Pager from '@/components/ui/Pager'
import { Input } from '@/components/ui/Field'
import { Select } from '@/components/ui/Select'
import Badge, { StateBadge } from '@/components/ui/Badge'
import ConfirmDialog from '@/components/ui/ConfirmDialog'
import { useToast } from '@/components/ui/toast'
import { invalidateOrderViews } from '@/components/order/lifecycle'
import { PendingRemarkModal } from '@/components/order/PendingRemarks'
import ProceedConfirmDialog from '@/components/order/ProceedConfirmDialog'
import { Table, Row, Cell, EmptyRow, actionsColumn, actionsCellClass } from '@/components/ui/Table'
import LogoLoader from '@/components/ui/LogoLoader'
import ShipmentModal, { toShipment, type ShipmentDraft } from '@/components/order/ShipmentModal'
import UrgentBadge from '@/components/job/UrgentBadge'
import PaymentBadge, { paymentStatusOptions } from '@/components/order/PaymentBadge'
import { VerdictBadge } from '@/components/admin/ai/AiBadges'
import { useOrderChecks } from '@/lib/aiInsights'

const statusOptions: { value: Order['status']; label: string }[] = [
  { value: 'pending_payment', label: 'Pending payment' },
  // Spelled exactly as StateBadge renders it, so the filter and the row chip
  // never look like two different states.
  { value: 'paid', label: 'Paid - awaiting production' },
  { value: 'in_production', label: 'In production' },
  { value: 'completed', label: 'Completed' },
  { value: 'issue', label: 'Issue' },
  { value: 'cancelled', label: 'Cancelled' },
]

/* Empty is "all", the Select placeholder row. '1' and '0' both filter: the
   server reads a present-but-zero urgent as "standard orders only". */
const urgentOptions = [
  { value: '1', label: 'Urgent only' },
  { value: '0', label: 'Not urgent' },
]

const mineOptions = [{ value: '1', label: 'Only my orders' }]

type OrderList = Paginated<Order> & { pending_count?: number }

interface ProceedManyResult {
  message: string
  results: { id: number; number: string; ok: boolean; message: string }[]
}

/** Still in the office: booked at its first section, not sent on yet. */
const isPending = (order: Order) => !!order.job?.awaiting_proceed

/**
 * The orders list, shared by the admin and sales consoles.
 *
 * Capabilities are passed in rather than checked against the signed-in role
 * inside, so an admin-only control added here later cannot silently appear in
 * the sales console: it has to be given a flag, and the flag has to be turned
 * on. The server enforces the same boundary - none of these routes exist under
 * /sales - so a flag left on is a broken button, never a hole.
 *
 * The Pending tab is the office's in-tray (client request 2026-10-08, laid
 * out like the Excard portal they already work): orders whose job the first
 * section has not received yet, oldest first, each with Proceed and the
 * remarks on why it is held. `canProceed` is 'all' for an admin and 'own'
 * for sales, who may act only on orders they took; the server decides either
 * way and its refusal is shown as is.
 */
export default function OrdersPage({
  basePath,
  canEditShipment,
  canOpenJobSheet,
  showMineFilter,
  canProceed,
  aiChecks = false,
}: {
  basePath: '/admin' | '/sales'
  canEditShipment: boolean
  canOpenJobSheet: boolean
  showMineFilter: boolean
  canProceed: 'all' | 'own' | false
  /** The MPG AI column and "Check all with MPG AI" on the Pending tab: admin console only. */
  aiChecks?: boolean
}) {
  const navigate = useNavigate()
  const location = useLocation()
  // The order page's Back link returns here, Pending tab included.
  const openOrder = (order: Order) => navigate(`${basePath}/orders/${order.id}`, { state: { from: location.pathname + location.search } })
  const { user } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [params, setParams] = useSearchParams()
  const pendingTab = params.get('tab') === 'pending'
  const [selected, setSelected] = useState<number[]>([])
  const [confirmMany, setConfirmMany] = useState(false)
  const [remarkFor, setRemarkFor] = useState<Order | null>(null)
  const [proceedFor, setProceedFor] = useState<Order | null>(null)
  const [search, setSearch] = useState('')
  const [mine, setMine] = useState('')
  const [urgent, setUrgent] = useState('')
  const [status, setStatus] = useState('')
  const [payment, setPayment] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [page, setPage] = useState(1)
  const [shipment, setShipment] = useState<ShipmentDraft | null>(null)

  // basePath is part of the key so the two consoles never share a cache entry.
  const { data, isLoading } = useQuery({
    queryKey: ['orders-page', basePath, pendingTab, search, status, payment, from, to, mine, urgent, page],
    queryFn: async () =>
      (
        await api.get<OrderList>(`${basePath}/orders`, {
          params: {
            page,
            ...(pendingTab ? { pending: 1 } : {}),
            ...(search ? { search } : {}),
            ...(status ? { status } : {}),
            ...(from ? { from } : {}),
            ...(to ? { to } : {}),
            ...(mine ? { mine } : {}),
            ...(urgent ? { urgent } : {}),
            ...(payment ? { payment } : {}),
          },
        })
      ).data,
  })

  const filter = (set: (value: string) => void) => (value: string) => {
    set(value)
    setPage(1)
  }

  const showTab = (pending: boolean) => {
    const next = new URLSearchParams(params)
    if (pending) next.set('tab', 'pending')
    else next.delete('tab')
    setParams(next, { replace: true })
    setPage(1)
    setSelected([])
  }

  // Sales may act only on orders they took; the server says so too.
  const mayAct = (order: Order) => canProceed === 'all' || (canProceed === 'own' && order.sales_user_id === user?.id)

  const proceedOne = useMutation({
    mutationFn: async (order: Order) => (await api.post<{ message: string }>(`${basePath}/orders/${order.id}/proceed`)).data,
    onSuccess: (res, order) => {
      toast.success(order.number, res.message)
      invalidateOrderViews(queryClient)
      setSelected((now) => now.filter((id) => id !== order.id))
      setProceedFor(null)
    },
    onError: (err) => toast.error('Could not proceed', errorMessage(err)),
  })

  const proceedMany = useMutation({
    mutationFn: async (ids: number[]) => (await api.post<ProceedManyResult>(`${basePath}/orders/proceed`, { order_ids: ids })).data,
    onSuccess: (res) => {
      const failed = res.results.filter((row) => !row.ok)
      if (failed.length === 0) toast.success(res.message)
      else toast.error(res.message, failed.map((row) => `${row.number}: ${row.message}`).join(' '))
      setConfirmMany(false)
      setSelected([])
      invalidateOrderViews(queryClient)
    },
    onError: (err) => toast.error('Could not proceed', errorMessage(err)),
  })

  // The newest MPG AI check per order on this page, polled while any is running.
  const checks = useOrderChecks(pendingTab ? (data?.data ?? []).map((order) => order.id) : [], aiChecks && pendingTab)
  const [confirmChecks, setConfirmChecks] = useState(false)
  const checkPending = useMutation({
    mutationFn: async () => (await api.post<{ message: string; queued: number }>('/admin/ai/checks/pending')).data,
    onSuccess: (res) => {
      toast.success('MPG AI', res.message)
      setConfirmChecks(false)
      queryClient.invalidateQueries({ queryKey: ['ai', 'checks'] })
    },
    onError: (err) => {
      toast.error('MPG AI could not start', errorMessage(err))
      setConfirmChecks(false)
    },
  })

  const pageIds = (data?.data ?? []).filter((order) => isPending(order) && mayAct(order)).map((order) => order.id)
  const allTicked = pageIds.length > 0 && pageIds.every((id) => selected.includes(id))
  const toggle = (id: number) => setSelected((now) => (now.includes(id) ? now.filter((x) => x !== id) : [...now, id]))
  const pendingCount = data?.pending_count ?? 0

  return (
    <div>
      <PageHeader
        title="Orders"
        subtitle="All customer orders across the platform"
        actions={
          <Link to={`${basePath}/orders/new`}>
            <Button>
              <Plus className="size-4" /> Create order
            </Button>
          </Link>
        }
      />

      <div className="mb-3 flex flex-wrap gap-2" role="tablist" aria-label="Order list">
        {[
          { pending: false, label: 'All' },
          { pending: true, label: 'Pending' },
        ].map((tab) => (
          <button
            key={tab.label}
            type="button"
            role="tab"
            aria-selected={pendingTab === tab.pending}
            onClick={() => showTab(tab.pending)}
            className={cn(
              'inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold transition',
              pendingTab === tab.pending
                ? 'border-primary/60 bg-primary/20 text-primary'
                : 'border-line bg-surface text-foreground/70 hover:bg-foreground/5',
            )}
          >
            {tab.label}
            {tab.pending && (
              <span className={cn('rounded-full px-1.5 text-2xs font-bold', pendingCount > 0 ? 'bg-pending text-white' : 'bg-foreground/10 text-foreground/60')}>
                {pendingCount}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-line bg-surface p-3">
        <Input
          placeholder="Search order, customer, job..."
          value={search}
          onChange={(e) => filter(setSearch)(e.target.value)}
          className="w-full min-w-0 sm:flex-1"
        />
        <Select
          value={status}
          onChange={filter(setStatus)}
          placeholder="All statuses"
          options={statusOptions}
          className="w-full sm:w-44"
        />
        <Select
          value={payment}
          onChange={filter(setPayment)}
          placeholder="Any payment"
          options={paymentStatusOptions}
          className="w-full sm:w-40"
        />
        <Select
          value={urgent}
          onChange={filter(setUrgent)}
          placeholder="Any urgency"
          options={urgentOptions}
          className="w-full sm:w-40"
        />
        {showMineFilter && (
          <Select
            value={mine}
            onChange={filter(setMine)}
            placeholder="All orders"
            options={mineOptions}
            className="w-full sm:w-40"
          />
        )}
        <Input
          type="date"
          title="Ordered from"
          aria-label="Ordered from"
          value={from}
          max={to || undefined}
          onChange={(e) => filter(setFrom)(e.target.value)}
          className="min-w-0 flex-1 sm:w-40 sm:flex-none"
        />
        <Input
          type="date"
          title="Ordered to"
          aria-label="Ordered to"
          value={to}
          min={from || undefined}
          onChange={(e) => filter(setTo)(e.target.value)}
          className="min-w-0 flex-1 sm:w-40 sm:flex-none"
        />
      </div>

      {isLoading ? (
        <LogoLoader />
      ) : pendingTab ? (
        <>
          {(canProceed || aiChecks) && (
            <div className="mb-3 flex flex-wrap items-center gap-2">
              {canProceed && (
                <Button disabled={selected.length === 0} onClick={() => setConfirmMany(true)}>
                  <Send className="size-4" /> Proceed selected{selected.length > 0 ? ` (${selected.length})` : ''}
                </Button>
              )}
              {aiChecks && (
                <Button variant="outline" disabled={pendingCount === 0} onClick={() => setConfirmChecks(true)}>
                  <Sparkles className="size-4" /> Check all with MPG AI
                </Button>
              )}
              {canProceed && <span className="text-xs text-muted">Proceeded after its production date, an order is re-planned from today.</span>}
            </div>
          )}
          <Table
            columns={[
              ...(canProceed
                ? [
                    {
                      label: (
                        <input
                          type="checkbox"
                          aria-label="Tick every order on this page"
                          checked={allTicked}
                          onChange={() => setSelected(allTicked ? [] : pageIds)}
                          className="size-4 accent-brand-500"
                        />
                      ),
                      className: 'w-px',
                    },
                  ]
                : []),
              'Order', 'Customer', 'Job sheet', 'Delivery due', ...(aiChecks ? ['MPG AI'] : []), 'PIC', 'Remark',
              ...(canProceed ? [actionsColumn] : []),
            ]}
          >
            {(data?.data.length ?? 0) === 0 && (
              <EmptyRow colSpan={(canProceed ? 8 : 6) + (aiChecks ? 1 : 0)}>Nothing pending - every order has been proceeded.</EmptyRow>
            )}
            {data?.data.map((order) => {
              const latest = order.pending_remarks?.[0]
              const more = (order.pending_remarks?.length ?? 0) - 1
              const allowed = mayAct(order)
              return (
                <Row key={order.id} onClick={() => openOrder(order)}>
                  {canProceed && (
                    <Cell>
                      <input
                        type="checkbox"
                        aria-label={`Tick ${order.number}`}
                        disabled={!allowed}
                        checked={selected.includes(order.id)}
                        onChange={() => toggle(order.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="size-4 accent-brand-500"
                      />
                    </Cell>
                  )}
                  <Cell className="whitespace-nowrap font-semibold">
                    {order.number}
                    <UrgentBadge urgent={order.is_urgent} className="ml-1.5" />
                    <p className="text-2xs font-normal text-muted">Ordered {formatDateTime(order.created_at)}</p>
                  </Cell>
                  <Cell>
                    <p>{order.agent?.name}</p>
                    <p className="text-2xs text-muted">{order.agent?.company_name}</p>
                  </Cell>
                  <Cell>
                    {order.job &&
                      (canOpenJobSheet ? (
                        <Link
                          to={`/admin/jobs/${order.job.id}/sheet?from=pending`}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="whitespace-nowrap font-mono text-xs font-bold text-brand-600 hover:underline"
                        >
                          {order.job.display_code ?? order.job.code}
                        </Link>
                      ) : (
                        <span className="whitespace-nowrap font-mono text-xs font-bold">{order.job.display_code ?? order.job.code}</span>
                      ))}
                    <div className="mt-1 whitespace-nowrap">
                      {order.job?.sheet_printed_at ? (
                        <Badge tone="success" className="text-2xs">
                          Printed {formatDateTime(order.job.sheet_printed_at)}
                        </Badge>
                      ) : (
                        <Badge tone="warning" className="text-2xs">
                          Not printed
                        </Badge>
                      )}
                    </div>
                  </Cell>
                  <Cell className="whitespace-nowrap">{formatDate(order.expected_completion_date)}</Cell>
                  {aiChecks && (
                    <Cell>
                      {checks.data?.[order.id] ? <VerdictBadge check={checks.data[order.id]} /> : <span className="text-xs text-muted">Not checked</span>}
                    </Cell>
                  )}
                  <Cell className="text-xs">{order.sales_person ?? '-'}</Cell>
                  <Cell className="max-w-56 text-xs">
                    {latest ? (
                      <>
                        <p className="whitespace-pre-line font-medium text-ink">{latest.remark}</p>
                        <p className="text-2xs text-muted">
                          {formatDateTime(latest.at)} - {latest.by ?? '-'}
                          {more > 0 && ` - +${more} more`}
                        </p>
                      </>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </Cell>
                  {canProceed && (
                    <Cell className={actionsCellClass}>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          className="px-2.5"
                          aria-label="Add a remark"
                          disabled={!allowed}
                          title={allowed ? 'Add a remark' : 'You can only do this on orders you took.'}
                          onClick={(e) => {
                            e.stopPropagation()
                            setRemarkFor(order)
                          }}
                        >
                          <MessageSquarePlus className="size-4" />
                          <span className="sr-only">Remark</span>
                        </Button>
                        <Button
                          disabled={!allowed}
                          title={allowed ? undefined : 'You can only do this on orders you took.'}
                          onClick={(e) => {
                            e.stopPropagation()
                            setProceedFor(order)
                          }}
                        >
                          <Send className="size-4" /> Proceed
                        </Button>
                      </div>
                    </Cell>
                  )}
                </Row>
              )
            })}
          </Table>
          <Pager meta={data?.meta} page={page} onPage={setPage} />
          <ConfirmDialog
            open={confirmMany}
            onClose={() => setConfirmMany(false)}
            onConfirm={() => proceedMany.mutate(selected)}
            loading={proceedMany.isPending}
            title={`Proceed ${selected.length} ${selected.length === 1 ? 'order' : 'orders'} to production?`}
            confirmLabel="Proceed"
          >
            Each leaves Pending and goes into its first section's queue, and that section is told. They cannot be put
            back to Pending. An order past its production date is re-planned from today.
          </ConfirmDialog>
          {aiChecks && (
            <ConfirmDialog
              open={confirmChecks}
              onClose={() => setConfirmChecks(false)}
              onConfirm={() => checkPending.mutate()}
              loading={checkPending.isPending}
              title="Check every pending order with MPG AI?"
              confirmLabel="Check all"
            >
              MPG AI checks each pending order not checked in the last day, up to 15 at a time. Each check is one of this
              month's questions and takes a minute or two; verdicts appear in the MPG AI column as they finish. Nothing
              changes on any order.
            </ConfirmDialog>
          )}
          <ProceedConfirmDialog
            order={
              proceedFor && {
                number: proceedFor.number,
                production_date: proceedFor.production_date,
                section: proceedFor.job?.current_department?.name,
              }
            }
            loading={proceedOne.isPending}
            onClose={() => setProceedFor(null)}
            onConfirm={() => proceedFor && proceedOne.mutate(proceedFor)}
          />
          <PendingRemarkModal
            basePath={basePath}
            order={remarkFor}
            remarks={remarkFor?.pending_remarks ?? []}
            onClose={() => setRemarkFor(null)}
          />
        </>
      ) : (
        <>
          <Table
            // Ten columns at the standard 16px a side overflowed a laptop-width card; 12px fits them
            className="sm:[&_td]:px-3 sm:[&_th]:px-3"
            columns={[
              'Order', 'Customer', 'Status', 'Payment', 'Job', 'Ordered', 'Delivery', 'PIC', 'Total',
              ...(canEditShipment ? [actionsColumn] : []),
            ]}
          >
            {(data?.data.length ?? 0) === 0 && <EmptyRow colSpan={canEditShipment ? 10 : 9} />}
            {data?.data.map((order) => (
              <Row key={order.id} onClick={() => openOrder(order)}>
                <Cell className="font-semibold">
                  <p className="whitespace-nowrap">{order.number}</p>
                  <UrgentBadge urgent={order.is_urgent} className="mt-1" />
                </Cell>
                <Cell>
                  <div className="min-w-24">
                    <p>{order.agent?.name}</p>
                    <p className="text-2xs text-muted">{order.agent?.company_name}</p>
                  </div>
                </Cell>
                <Cell>
                  {/* Wide enough that "paid - awaiting production" breaks once, not after every word */}
                  <div className="min-w-28">
                    {isPending(order) ? <Badge tone="info">Pending</Badge> : <StateBadge state={order.status} />}
                  </div>
                </Cell>
                <Cell>
                  <PaymentBadge status={order.payment_status} />
                </Cell>
                <Cell className="whitespace-nowrap">
                  {order.job &&
                    (canOpenJobSheet ? (
                      <Link
                        to={`/admin/jobs/${order.job.id}/sheet?from=orders`}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="font-mono text-xs font-bold text-brand-600 hover:underline"
                      >
                        {order.job.display_code ?? order.job.code}
                      </Link>
                    ) : (
                      <span className="font-mono text-xs font-bold">{order.job.display_code ?? order.job.code}</span>
                    ))}
                </Cell>
                <Cell className="whitespace-nowrap text-xs">{formatDateTimeShort(order.created_at)}</Cell>
                <Cell className="text-xs">{order.delivery_method ?? '-'}</Cell>
                <Cell className="text-xs">{order.sales_person ?? '-'}</Cell>
                <Cell className="whitespace-nowrap font-semibold">{money(order.total)}</Cell>
                {canEditShipment && (
                  <Cell className={actionsCellClass}>
                    <Button
                      variant="ghost"
                      className="px-2"
                      title="Shipment / courier tracking"
                      onClick={(e) => {
                        e.stopPropagation()
                        setShipment(toShipment(order))
                      }}
                    >
                      <Truck className="size-4" />
                    </Button>
                  </Cell>
                )}
              </Row>
            ))}
          </Table>
          <Pager meta={data?.meta} page={page} onPage={setPage} />
        </>
      )}

      {canEditShipment && (
        <ShipmentModal shipment={shipment} onChange={setShipment} onClose={() => setShipment(null)} />
      )}
    </div>
  )
}
